import type { Revision } from '@/components/domain/RevisionRow';

// Shape served by GET /api/revisions → storeRevisions
// (ghostfit-console/lib/store-revision-queue.js buildStoreRevisionQueue).
export type StoreRevision = {
  kind: 'store';
  storeId: string;
  requestId: string;
  storeName: string;
  storeStatus: string | null;
  type: string;
  status: string;
  notes: string;
  requestedAt: string | null;
  requestedBy: string | null;
  revisionNumber: number | null;
  source: 'proof_page' | 'operator';
  fileCount: number;
  // "What was asked" when there are no notes (details, typed items, or type).
  detailSummary: string;
};

export type QueueItem =
  | { kind: 'prize'; key: string; at: string; prize: Revision }
  | { kind: 'store'; key: string; at: string; store: StoreRevision };

// When the customer asked, as a sortable ISO string. requestedit stamps
// lastRevisionAt on every revision; createdAt is the submission's age.
function prizeTime(p: Revision): string {
  const t = [p.lastRevisionAt, p.createdAt].find((v) => typeof v === 'string');
  return t || '';
}

// One list, most-recently-asked first. Prize rows sort on lastRevisionAt (the
// ask), falling back to createdAt only when it's missing; Array.prototype.sort
// is stable, so prize order among ties is unchanged by the merge.
export function mergeRevisionQueue(prizes: Revision[], stores: StoreRevision[]): QueueItem[] {
  const items: QueueItem[] = [
    ...prizes.map((p) => ({ kind: 'prize' as const, key: `prize:${p.id}`, at: prizeTime(p), prize: p })),
    ...stores.map((s) => ({
      kind: 'store' as const,
      key: `store:${s.storeId}:${s.requestId}`,
      at: typeof s.requestedAt === 'string' ? s.requestedAt : '',
      store: s,
    })),
  ];
  return items.sort((a, b) => b.at.localeCompare(a.at));
}

export function matchesQuery(item: QueueItem, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const hay = item.kind === 'prize'
    ? [item.prize.school_name, item.prize.sportType, item.prize.rep_change_notes, item.prize.lastRevisionNotes]
    : [item.store.storeName, item.store.notes, item.store.detailSummary, item.store.type];
  return hay.some((v) => String(v || '').toLowerCase().includes(needle));
}
